import React, { lazy, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { type Route, type Routes, useRouter } from 'react-resource-router';

import SectionMessage from '@atlaskit/section-message/message';
import Spinner from '@atlaskit/spinner/spinner';

import { getConfig } from '../../../config';
import { NoAppsAvailablePage } from '../../components/NoAppsAvailablePage';
import { messages } from '../../pages/graphclaw-hub/messages';
import { fetchGraphClawInferenceAppPermissions } from '../../pages/graphclaw-hub/service';
import type {
	GraphClawHubView,
	GraphClawInferenceAppPermissions,
} from '../../pages/graphclaw-hub/types';
import { useGraphClawHubMockMode } from '../../pages/graphclaw-hub/use-graphclaw-hub-mock-mode';
import { componentWithSuspense } from '../../utils';

const GraphClawHubPage = lazy(
	() =>
		import(
			/* webpackChunkName: "@atlaskit-internal_dtac-graphclaw-hub" */ '../../pages/graphclaw-hub'
		),
);

const PacketReviewPage = lazy(() =>
	import(
		/* webpackChunkName: "@atlassian/graphclaw-packet-review-route" */ '../../pages/graphclaw-hub/components/ManagedReviewWorkspaceEntry'
	).then((module) => ({ default: module.ManagedReviewWorkspaceEntry })),
);

const viewByRouteName: Record<string, GraphClawHubView> = {
	'graphclaw-hub': 'inferences',
	'graphclaw-hub-apps': 'apps',
	'graphclaw-hub-inference-details': 'inference-details',
	'graphclaw-hub-inferences': 'inferences',
	'graphclaw-hub-review': 'review',
	'graphclaw-hub-my-inference': 'my-inference',
	'graphclaw-hub-my-inference-app': 'app-detail',
	'graphclaw-hub-dashboard': 'dashboard',
	'graphclaw-hub-registry': 'registry',
	'graphclaw-hub-try-it': 'try-it',
	'graphclaw-hub-audit-log': 'audit-log',
};

const mockPermissions: GraphClawInferenceAppPermissions = {
	canView: true,
	canManage: true,
	canRunInference: true,
};

const GraphClawHubPageWithPermissions = ({
	view,
}: {
	view: GraphClawHubView;
}): React.JSX.Element => {
	const intl = useIntl();
	const isMockMode = useGraphClawHubMockMode();
	const [permissions, setPermissions] = useState<GraphClawInferenceAppPermissions | null>(null);
	const [hasPermissionError, setHasPermissionError] = useState(false);

	useEffect(() => {
		if (isMockMode) {
			setPermissions(mockPermissions);
			setHasPermissionError(false);
			return;
		}

		let active = true;
		if (isMockMode) {
			setPermissions(mockPermissions);
			setHasPermissionError(false);
			return () => {
				active = false;
			};
		}

		setPermissions(null);
		setHasPermissionError(false);
		void fetchGraphClawInferenceAppPermissions()
			.then((result) => {
				if (active) {
					setPermissions(result);
					setHasPermissionError(false);
				}
			})
			.catch(() => {
				if (active) {
					setHasPermissionError(true);
				}
			});
		return () => {
			active = false;
		};
	}, [isMockMode]);

	if (hasPermissionError) {
		return (
			<SectionMessage appearance="error" title={intl.formatMessage(messages.errorHeader)}>
				{intl.formatMessage(messages.errorDescription)}
			</SectionMessage>
		);
	}
	if (!permissions) {
		return <Spinner size="large" label={intl.formatMessage(messages.loadingLabel)} />;
	}
	if (!permissions.canView || (view === 'try-it' && !permissions.canRunInference)) {
		return <NoAppsAvailablePage />;
	}

	return <GraphClawHubPage view={view} permissions={permissions} />;
};

const GraphClawHubPageWithAccess = (): React.JSX.Element => {
	const [{ route }] = useRouter();
	const view = viewByRouteName[route.name] ?? 'inferences';
	const isMockMode = useGraphClawHubMockMode();
	const packetId =
		typeof window === 'undefined'
			? null
			: new URLSearchParams(window.location.search).get('packetId');
	useEffect(() => {
		if (route.name === 'graphclaw-hub') {
			globalThis.history.replaceState(
				null,
				'',
				`/graphclaw-hub/inferences${globalThis.location.search}`,
			);
			globalThis.dispatchEvent(new PopStateEvent('popstate'));
		}
	}, [route.name]);

	// Packet authorization comes from entry/context resolvers, independently of app management access.
	if (view === 'review' && packetId?.trim())
		return <PacketReviewPage isMock={isMockMode && getConfig().isLocalDev} />;
	return <GraphClawHubPageWithPermissions view={view} />;
};

const graphClawHubRouteComponent = componentWithSuspense(GraphClawHubPageWithAccess);

const ROUTE_PREFIX = '/graphclaw-hub' as const;

const graphClawHubRoutesObject: Record<string, Route> = {
	home: {
		name: 'graphclaw-hub',
		path: ROUTE_PREFIX,
		exact: true,
		component: graphClawHubRouteComponent,
	},
	inferences: {
		name: 'graphclaw-hub-inferences',
		path: `${ROUTE_PREFIX}/inferences` as const,
		exact: true,
		component: graphClawHubRouteComponent,
	},
	apps: {
		name: 'graphclaw-hub-apps',
		path: `${ROUTE_PREFIX}/apps`,
		exact: true,
		component: graphClawHubRouteComponent,
	},
	inferenceDetails: {
		name: 'graphclaw-hub-inference-details',
		path: `${ROUTE_PREFIX}/inferences/details`,
		exact: true,
		component: graphClawHubRouteComponent,
	},
	review: {
		name: 'graphclaw-hub-review',
		path: `${ROUTE_PREFIX}/review` as const,
		exact: true,
		component: graphClawHubRouteComponent,
	},
	myInference: {
		name: 'graphclaw-hub-my-inference',
		path: `${ROUTE_PREFIX}/my-inference` as const,
		exact: true,
		component: graphClawHubRouteComponent,
	},
	myInferenceApp: {
		name: 'graphclaw-hub-my-inference-app',
		path: `${ROUTE_PREFIX}/my-inference/:appId` as const,
		exact: true,
		component: graphClawHubRouteComponent,
	},
	dashboard: {
		name: 'graphclaw-hub-dashboard',
		path: `${ROUTE_PREFIX}/dashboard` as const,
		exact: true,
		component: graphClawHubRouteComponent,
	},
	registry: {
		name: 'graphclaw-hub-registry',
		path: `${ROUTE_PREFIX}/registry` as const,
		exact: true,
		component: graphClawHubRouteComponent,
	},
	tryIt: {
		name: 'graphclaw-hub-try-it',
		path: `${ROUTE_PREFIX}/try-it` as const,
		exact: true,
		component: graphClawHubRouteComponent,
	},
	auditLog: {
		name: 'graphclaw-hub-audit-log',
		path: `${ROUTE_PREFIX}/audit-log` as const,
		exact: true,
		component: graphClawHubRouteComponent,
	},
};

export const graphClawHubRoutes: Routes = Object.values(graphClawHubRoutesObject);
